import {
  AccountDeletionStatusResponseSchema,
  ApiErrorSchema,
  UpdateLocaleResponseSchema,
  type AccountDeletionStatus,
  type Locale,
  type RequestAccountDeletionInput,
} from '@ah/contracts';

/** Thin fetch wrapper for self-service account deletion (ADR-012) and the ADR-015 locale preference. Same shape as the mobile client's. */
export class AccountApi {
  constructor(private readonly baseUrl: string) {}

  /** PATCH /api/v1/account/locale (ADR-015 §3) — persists the UI-language preference server-side so it follows the user across devices. */
  async updateLocale(accessToken: string, preferredLocale: Locale): Promise<Locale> {
    const res = await fetch(`${this.baseUrl}/api/v1/account/locale`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ preferredLocale }),
    });
    if (!res.ok) throw await this.toError(res);
    return UpdateLocaleResponseSchema.parse(await res.json()).preferredLocale;
  }

  async requestDeletion(accessToken: string, input: RequestAccountDeletionInput): Promise<AccountDeletionStatus> {
    return this.postAuthed(accessToken, '/api/v1/account/deletion-request', input);
  }

  async cancelDeletion(accessToken: string): Promise<AccountDeletionStatus> {
    return this.postAuthed(accessToken, '/api/v1/account/deletion-cancel', {});
  }

  async getStatus(accessToken: string): Promise<AccountDeletionStatus> {
    const res = await fetch(`${this.baseUrl}/api/v1/account/deletion-status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    const parsed = AccountDeletionStatusResponseSchema.parse(await res.json());
    return parsed.deletion;
  }

  private async postAuthed(accessToken: string, path: string, body: unknown): Promise<AccountDeletionStatus> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await this.toError(res);
    const parsed = AccountDeletionStatusResponseSchema.parse(await res.json());
    return parsed.deletion;
  }

  private async toError(res: Response): Promise<Error> {
    try {
      const payload = ApiErrorSchema.parse(await res.json());
      return new AccountApiError(payload.error.code, payload.error.message, res.status);
    } catch {
      return new AccountApiError('INTERNAL_ERROR', res.statusText, res.status);
    }
  }
}

export class AccountApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AccountApiError';
  }
}
